/**
 * Что портал спрашивает у сервера — контракт P1, §5.2 ТЗ портала.
 *
 * Вход, выход и «кто я» — клиент каркаса (`@shell/api`). Здесь — меню по
 * правам (`platform-api`) и «Накладные» (`ops-api`). Свой тонкий `request`
 * нужен по двум причинам, обе про контракт P1:
 *
 * - правка строк черновика идёт методом `PATCH`, которого клиент каркаса
 *   не знает;
 * - причина отказа приходит полем `detail` («уже отмечено», «черновик уже
 *   создан»), и человек должен прочитать её, а не код статуса.
 *
 * Разбор `401`/`403` и повторы — те же, что у каркаса: ошибка — это
 * `ApiError` каркаса, и `createQueryClient` каркаса её понимает.
 *
 * Суммы — строками в рублях с копейками и `null`, если у человека нет
 * флага денег. Арифметики над деньгами в браузере нет.
 */

import { ApiError } from "@shell/api";
import { getInitData } from "@shell/miniapp";

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface PortalRequest {
  method?: Method;
  body?: unknown;
}

const MOCK = import.meta.env.VITE_MOCK_API === "1";

export async function request<T>(
  path: string,
  options: PortalRequest = {},
): Promise<T> {
  if (MOCK) {
    const { mockPortal } = await import("./mock");
    return mockPortal<T>(path, options);
  }
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["content-type"] = "application/json";
  const initData = getInitData();
  if (initData) headers["X-Telegram-Init-Data"] = initData;

  const response = await fetch(path, {
    method: options.method ?? "GET",
    credentials: "include",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Запрос ${path} завершился со статусом ${response.status}`,
      await readDetail(response),
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Загрузка файла — телом запроса, а не `multipart/form-data`.
 *
 * Одно поле, один файл, ноль зависимостей на разбор формы: так же
 * загружаются фотографии блюд и импорт справочника у `study`. Сервер
 * узнаёт вид файла из `content-type`, а не из имени — имя человек может
 * назвать как угодно.
 */
export async function upload<T>(path: string, file: Blob): Promise<T> {
  if (MOCK) {
    const { mockPortal } = await import("./mock");
    return mockPortal<T>(path, {
      method: "POST",
      body: { bytes: file.size, type: file.type },
    });
  }
  const headers: Record<string, string> = {
    "content-type": file.type || "application/octet-stream",
  };
  const initData = getInitData();
  if (initData) headers["X-Telegram-Init-Data"] = initData;

  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers,
    body: file,
  });
  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Загрузка ${path} завершилась со статусом ${response.status}`,
      await readDetail(response),
    );
  }
  return (await response.json()) as T;
}

async function readDetail(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as {
      detail?: unknown;
      message?: unknown;
    };
    for (const value of [body?.detail, body?.message]) {
      if (typeof value === "string" && value.trim()) return value;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Адрес фото для `<img>`. В браузере cookie прикладывается сама; внутри
 * Telegram личность едет заголовком, которого `<img>` не умеет, — тогда
 * снимок скачивается запросом и показывается из памяти.
 */
export async function photoObjectUrl(url: string): Promise<string> {
  const initData = getInitData();
  if (MOCK || !initData || url.startsWith("data:")) return url;
  const response = await fetch(url, {
    credentials: "include",
    headers: { "X-Telegram-Init-Data": initData },
  });
  if (!response.ok) throw new ApiError(response.status, "Фото не загрузилось");
  return URL.createObjectURL(await response.blob());
}

/* ── Исправления приходов (O15, D268) ───────────────────────────────── */

export type CorrectionGroup = "open" | "progress" | "closed";

export interface CorrectionSide {
  goods: string;
  unit: string;
  quantity: string;
  net: string | null;
  vat: string | null;
  vat_rate: number;
  price: string | null;
}

export interface CorrectionEdit {
  kind: "quantity" | "vat" | "goods" | "delete" | "insert";
  title: string;
  before: CorrectionSide | null;
  after: CorrectionSide | null;
  reason: string;
  paper: string;
  photos: string[];
}

export interface Correction {
  id: number;
  status:
    | "prepared"
    | "confirmed"
    | "rejected"
    | "writing"
    | "done"
    | "failed"
    | "stale";
  document: {
    date: string | null;
    number: string | null;
    supplier: string | null;
    posted: boolean;
    receipt_id: number | null;
  };
  note: string;
  edits: CorrectionEdit[];
  /** Фото бумаги для просмотрщика — как страницы карточки накладной. */
  pages: ReceiptDetail["pages"];
  prepared_at: string;
  decided_at: string | null;
  reject_reason: string | null;
  verified_at: string | null;
  failure: string | null;
}

export interface CorrectionList {
  items: Correction[];
  counts: Record<CorrectionGroup, number>;
  may_confirm: boolean;
}

/* ── Меню по правам ─────────────────────────────────────────────────── */

export type RightValue = "view" | "edit" | string;

export interface ServiceRights {
  level: string | null;
  rights: Record<string, RightValue>;
}

export interface Navigation {
  user: {
    id: number;
    name: string | null;
    is_host: boolean;
    /** «Команда»: владелец правит, управляющий смотрит (§2 ТЗ портала). */
    can_view_team: boolean;
    /** Копировать и выгружать (D233). Нет — замок и водяной знак. */
    can_copy?: boolean;
  };
  services: Partial<Record<"ops" | "finance" | "study", ServiceRights>>;
  /**
   * Имена ботов в Telegram — из настроек сервера, а не строками здесь.
   *
   * До 20.09.2026 портал знал их сам (`t.me/toscana_ops_bot` и два
   * соседних), и второй ресторан получил бы ссылки на чужие боты. Бота,
   * которого нет в настройках, в ответе нет вовсе: ссылка в никуда хуже
   * её отсутствия.
   */
  bots?: Partial<Record<"ops" | "finance" | "study", string>>;
}

/* ── Сегодня ────────────────────────────────────────────────────────── */

export type TodayKind =
  | "receipt_check"
  | "receipt_ready"
  | "receipt_waiting"
  | "receipt_posted"
  | "duplicate"
  | "question"
  | "task";

export interface TodayItem {
  kind: TodayKind;
  title: string;
  subtitle: string | null;
  link: string | null;
  colour: string | null;
}

export interface Today {
  items: TodayItem[];
  counts: Record<string, number>;
}

/* ── Спросить агента ────────────────────────────────────────────────── */

/* ── Накладные ────────────────────────────────────────────────────────── */

export type SectionCode = "check" | "ready" | "wait" | "posted" | "dup";

export interface QueueItem {
  id: number;
  supplier: string | null;
  title: string;
  reason: string | null;
  colour: string | null;
  received_at: string | null;
  receiver: string | null;
  draft_label: string | null;
}

export interface DuplicateGroup {
  key: string;
  pair_id: number;
  supplier: string | null;
  number: string;
  dates: string[];
  count: number;
  same_total: boolean;
  posted: number;
  advice: string | null;
  stale_mark: boolean;
}

export interface Queue {
  counts: Record<SectionCode | "questions", number>;
  items: QueueItem[];
  duplicates: DuplicateGroup[];
}

export interface ProposalLine {
  number: number;
  paper_name: string | null;
  goods: number | null;
  goods_name: string | null;
  unit: number | null;
  unit_name: string | null;
  quantity: string | null;
  net: string | null;
  vat: string | null;
  /** В сотых долях процента, как в StoreHouse: 500 = 5 %. */
  vat_rate: number | null;
  /** С НДС: без НДС плюс НДС. */
  gross: string | null;
  notes: string[];
  ready: boolean;
}

export interface Proposal {
  id: number;
  status: string;
  header: {
    number: string | null;
    date: string | null;
    recipient: number | null;
    recipient_name: string | null;
    paper_total: string | null;
    /** Другие бумаги на фото той же приёмки — в черновик не вошли. */
    other_papers?: { total: string | null; names: string[] }[];
  };
  lines: ProposalLine[];
  ready: boolean;
  total: string | null;
  net_total: string | null;
  /** Ставки, которые можно выбрать, в сотых долях процента. */
  vat_rates: number[];
}

export type ReceiptAction =
  "verify" | "posted" | "posted_ok" | "posted_error" | "prepare_draft";

/** Строка бумаги, как её прочитал агент. */
export interface PaperLine {
  name: string;
  quantity: string | null;
  price: string | null;
  amount: string | null;
}

/** Строка документа StoreHouse. */
export interface DraftLine {
  goods: string;
  unit: string | null;
  quantity: string | null;
  net: string | null;
  vat: string | null;
}

/** Строка сравнения «бумага — документ»: пары те же, что у проверки строк. */
export interface ComparedLine {
  state: "same" | "paper_only" | "draft_only";
  paper: PaperLine | null;
  draft: DraftLine | null;
}

export interface ReceiptDetail {
  id: number;
  status: string;
  colour: string | null;
  section: SectionCode | null;
  supplier: { id: number; name: string } | null;
  new_supplier_name: string | null;
  received_at: string | null;
  receiver: string | null;
  note: string | null;
  draft: {
    invoice_id: number;
    label: string;
    number: string | null;
    date: string | null;
    total: string | null;
    posted: boolean;
  } | null;
  pages: {
    photo_id: number;
    url: string;
    width: number | null;
    height: number | null;
  }[];
  paper: {
    number: string | null;
    date: string | null;
    total: string | null;
    lines: PaperLine[];
  } | null;
  draft_lines: DraftLine[];
  /** Строки документа ещё не читались из StoreHouse — не то же, что «строк нет». */
  draft_lines_pending: boolean;
  reasons: string[];
  skipped: string[];
  verified: boolean;
  /** Шаг карточки — тот же, по которому бот строит свои кнопки. */
  step: string;
  /** Кто завёл документ в StoreHouse и кто его провёл. */
  origin: {
    created_by: "agent" | "storehouse";
    posted_by: "agent" | "human" | null;
    text: string;
  } | null;
  /** Что с накладной и что сделать — словами, по шагам. */
  guide: { title: string; text: string; steps: string[] } | null;
  /** Разница итогов бумаги и документа; только с флагом денег. */
  difference: {
    paper: string | null;
    draft: string | null;
    amount: string | null;
    direction: "paper_more" | "draft_more";
    hint: string | null;
  } | null;
  /** Строки парами; `null` — сравнивать нечем, показываются две таблицы. */
  comparison: ComparedLine[] | null;
  proposal: Proposal | null;
  questions: ReceiptQuestion[];
  /** ИНН с фото — только у приёмки нового поставщика. */
  paper_inn: string | null;
  actions: ReceiptAction[];
}

/** Вопрос агента по накладной — ответы те же, что кнопками в боте. */
export interface ReceiptQuestion {
  id: number;
  text: string;
  answers: { code: string; title: string }[];
}

export interface Supplier {
  id: number;
  name: string;
}

export interface Goods {
  goods_id: number;
  name: string;
  unit_id: number | null;
  unit_name: string | null;
}

export interface Recipient {
  id: number;
  name: string;
}

export interface Message {
  message: string;
}

/** Находка поиска по фото накладных (`GET /api/ops/archive`). */
export interface ArchiveItem {
  receipt_id: number;
  status: string;
  received_at: string | null;
  supplier: string;
  invoice_number: string | null;
  invoice_date: string | null;
  /** Без права «Оплаты» — `null`. */
  invoice_total: string | null;
  is_posted: boolean | null;
  paper_numbers: string[];
  paper_dates: string[];
  pages: number;
  first_photo_id: number | null;
  /** Где совпало: «номер», «текст на фото», «сумма в системе»… */
  why: string[];
}

export interface ArchiveResult {
  items: ArchiveItem[];
  more: boolean;
}

export interface LinePatch {
  goods_id?: number;
  quantity?: string;
  amount?: string;
  vat_rate?: number;
  gross?: string;
  /** «Верно» — пересчёт единиц правильный. */
  confirm?: boolean;
}

/**
 * Все обращения — полями объекта, а не отдельными функциями: экраны зовут
 * `portalApi.x(...)` в момент запроса, и тест подменяет ровно один адрес.
 */
export const portalApi = {
  navigation: () => request<Navigation>("/api/platform/navigation"),
  today: () => request<Today>("/api/ops/today"),
  queue: (section: SectionCode) =>
    request<Queue>(`/api/ops/receipts?section=${encodeURIComponent(section)}`),
  receipt: (id: number) => request<ReceiptDetail>(`/api/ops/receipts/${id}`),
  mark: (id: number, action: Exclude<ReceiptAction, "prepare_draft">) =>
    request<Message>(`/api/ops/receipts/${id}/marks`, {
      method: "POST",
      body: { action },
    }),
  prepare: (id: number) =>
    request<Proposal>(`/api/ops/receipts/${id}/prepare`, { method: "POST" }),
  archive: (params: {
    q?: string;
    since?: string;
    until?: string;
    amount?: string;
  }) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(
      ([key, value]) => value && search.set(key, value),
    );
    return request<ArchiveResult>(`/api/ops/archive?${search.toString()}`);
  },
  suppliers: (q: string) =>
    request<Supplier[]>(`/api/ops/suppliers?q=${encodeURIComponent(q)}`),
  setSupplier: (id: number, supplierId: number) =>
    request<Message>(`/api/ops/receipts/${id}/supplier`, {
      method: "POST",
      body: { supplier_id: supplierId },
    }),
  recheckSupplier: (id: number) =>
    request<Message>(`/api/ops/receipts/${id}/supplier/recheck`, {
      method: "POST",
    }),
  answerQuestion: (id: number, questionId: number, code: string) =>
    request<Message>(`/api/ops/receipts/${id}/questions/${questionId}/answer`, {
      method: "POST",
      body: { code },
    }),
  closeReceipt: (id: number) =>
    request<Message>(`/api/ops/receipts/${id}/close`, { method: "POST" }),
  rotatePhoto: (id: number, photoId: number, degrees: number) =>
    request<{
      photo_id: number;
      url: string;
      width: number | null;
      height: number | null;
      message: string;
    }>(`/api/ops/receipts/${id}/photos/${photoId}/rotate`, {
      method: "POST",
      body: { degrees },
    }),
  duplicateDeleted: (pairId: number) =>
    request<Message>(`/api/ops/duplicates/${pairId}/deleted`, {
      method: "POST",
    }),
  duplicateDifferent: (pairId: number) =>
    request<Message>(`/api/ops/duplicates/${pairId}/different`, {
      method: "POST",
    }),
  goods: (q: string) =>
    request<Goods[]>(`/api/ops/goods?q=${encodeURIComponent(q)}`),
  suggestions: (proposalId: number, n: number) =>
    request<Goods[]>(`/api/ops/proposals/${proposalId}/lines/${n}/suggestions`),
  patchLine: (proposalId: number, n: number, patch: LinePatch) =>
    request<Proposal>(`/api/ops/proposals/${proposalId}/lines/${n}`, {
      method: "PATCH",
      body: patch,
    }),
  addLine: (proposalId: number, goodsId: number) =>
    request<Proposal>(`/api/ops/proposals/${proposalId}/lines`, {
      method: "POST",
      body: { goods_id: goodsId },
    }),
  deleteLine: (proposalId: number, n: number) =>
    request<Proposal>(`/api/ops/proposals/${proposalId}/lines/${n}`, {
      method: "DELETE",
    }),
  patchProposal: (
    proposalId: number,
    patch: { recipient?: number; number?: string; date?: string },
  ) =>
    request<Proposal>(`/api/ops/proposals/${proposalId}`, {
      method: "PATCH",
      body: patch,
    }),
  recipients: () => request<Recipient[]>("/api/ops/recipients"),
  corrections: (group: CorrectionGroup) =>
    request<CorrectionList>(`/api/ops/corrections?group=${group}`),
  confirmCorrection: (id: number) =>
    request<Correction>(`/api/ops/corrections/${id}/confirm`, {
      method: "POST",
    }),
  rejectCorrection: (id: number, reason: string) =>
    request<Correction>(`/api/ops/corrections/${id}/reject`, {
      method: "POST",
      body: { reason },
    }),
  createDraft: (proposalId: number) =>
    request<Message>(`/api/ops/proposals/${proposalId}/create`, {
      method: "POST",
    }),
  cancelProposal: (proposalId: number) =>
    request<Message>(`/api/ops/proposals/${proposalId}/cancel`, {
      method: "POST",
    }),
};

/** Рубли из строки сервера: «21 090,46 ₽». `null` — сумм не показываем. */
export function rub(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return (
    new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number) + " ₽"
  );
}

/** Количество без лишних нулей: «12,5». */
export function qty(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 3 }).format(
    number,
  );
}
